import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, CheckCircle } from "@phosphor-icons/react";
import { fmtYmd } from "@/lib/dateFormat";
import VehicleQrField from "@/components/VehicleQrField";

export default function Partners() {
    const [partners, setPartners] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [pOpen, setPOpen] = useState(false);
    const [qOpen, setQOpen] = useState(false);
    const [form, setForm] = useState({ name: "", contact_person: "", phone: "", email: "", specialty: "", address: "" });
    const [qForm, setQForm] = useState({ partner_id: "", vehicle_id: "", description: "", amount_yen: 0 });

    const load = async () => {
        const [p, q, v] = await Promise.all([api.get("/partners"), api.get("/partners/quotes"), api.get("/vehicles")]);
        setPartners(p.data); setQuotes(q.data); setVehicles(v.data);
    };
    useEffect(() => { load(); }, []);

    const createPartner = async (e) => {
        e.preventDefault();
        try {
            await api.post("/partners", form);
            setPOpen(false);
            setForm({ name: "", contact_person: "", phone: "", email: "", specialty: "", address: "" });
            toast.success("登録しました");
            await load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const createQuote = async (e) => {
        e.preventDefault();
        try {
            await api.post("/partners/quotes", { ...qForm, amount_yen: parseInt(qForm.amount_yen || 0) });
            setQOpen(false);
            setQForm({ partner_id: "", vehicle_id: "", description: "", amount_yen: 0 });
            toast.success("見積を依頼しました");
            await load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const approveQuote = async (id) => {
        try {
            await api.post(`/partners/quotes/${id}/approve`);
            toast.success("承認しました");
            await load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const delPartner = async (id) => {
        if (!window.confirm("削除しますか？")) return;
        try { await api.delete(`/partners/${id}`); await load(); toast.success("削除しました"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <div className="space-y-5" data-testid="partners-page">
            <div>
                <p className="prc-label-overline">整備工場連携</p>
                <h1 className="text-[1.685rem] font-bold tracking-tight">パートナー管理</h1>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                <div className="prc-card p-5">
                    <div className="flex justify-between items-center mb-3">
                        <p className="prc-label-overline">整備工場一覧</p>
                        <button onClick={() => setPOpen(true)} className="prc-btn-ghost text-xs inline-flex items-center gap-1" data-testid="partner-new-btn"><Plus size={16} />追加</button>
                    </div>
                    <div className="divide-y divide-[var(--prc-border)]" data-testid="partners-list">
                        {partners.length === 0 ? <p className="text-sm text-[var(--prc-muted)]">未登録</p> :
                            partners.map((p) => (
                                <div key={p.id} className="py-3 flex justify-between items-start" data-testid={`partner-${p.id}`}>
                                    <div>
                                        <p className="font-semibold text-sm">{p.name}</p>
                                        <p className="text-xs text-[var(--prc-muted)]">{p.specialty || "-"} · {p.phone || "-"}</p>
                                        <p className="text-xs text-[var(--prc-tertiary)]">{p.address || ""}</p>
                                    </div>
                                    <button onClick={() => delPartner(p.id)} className="text-xs text-[var(--prc-red)]" data-testid={`partner-del-${p.id}`}><Trash size={16} /></button>
                                </div>
                            ))}
                    </div>
                </div>

                <div className="prc-card p-5">
                    <div className="flex justify-between items-center mb-3">
                        <p className="prc-label-overline">見積・承認ワークフロー</p>
                        <button onClick={() => setQOpen(true)} className="prc-btn-ghost text-xs inline-flex items-center gap-1" data-testid="quote-new-btn"><Plus size={16} />依頼</button>
                    </div>
                    <div className="divide-y divide-[var(--prc-border)]" data-testid="quotes-list">
                        {quotes.length === 0 ? <p className="text-sm text-[var(--prc-muted)]">依頼なし</p> :
                            quotes.map((q) => (
                                <div key={q.id} className="py-3" data-testid={`quote-${q.id}`}>
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <p className="text-sm">{q.description}</p>
                                            <p className="text-xs text-[var(--prc-muted)] mt-0.5">
                                                ¥{q.amount_yen.toLocaleString()} · {fmtYmd(q.created_at, String(q.created_at).substring(0, 10))}
                                            </p>
                                        </div>
                                        {q.status === "approved" ? <span className="prc-badge ok">承認済</span>
                                            : <button onClick={() => approveQuote(q.id)} className="prc-btn-ghost text-xs" data-testid={`quote-approve-${q.id}`}>承認</button>}
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            </div>

            {pOpen && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="partner-dialog">
                    <form onSubmit={createPartner} className="prc-card p-6 max-w-md w-full">
                        <h2 className="text-xl font-bold mb-3">整備工場の追加</h2>
                        <div className="space-y-3">
                            <F label="工場名 *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="p-name" />
                            <F label="担当者" value={form.contact_person} onChange={(v) => setForm({ ...form, contact_person: v })} testid="p-contact" />
                            <F label="電話" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="p-phone" />
                            <F label="メール" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="p-email" />
                            <F label="得意分野" value={form.specialty} onChange={(v) => setForm({ ...form, specialty: v })} testid="p-specialty" />
                            <F label="住所" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="p-address" />
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button type="button" onClick={() => setPOpen(false)} className="prc-btn-ghost">キャンセル</button>
                            <button type="submit" className="prc-btn-primary">登録</button>
                        </div>
                    </form>
                </div>
            )}

            {qOpen && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="quote-dialog">
                    <form onSubmit={createQuote} className="prc-card p-6 max-w-md w-full">
                        <h2 className="text-xl font-bold mb-3">見積依頼</h2>
                        <div className="space-y-3">
                            <div>
                                <label className="prc-label-overline block mb-1">整備工場 *</label>
                                <select required className="prc-input" value={qForm.partner_id} onChange={(e) => setQForm({ ...qForm, partner_id: e.target.value })} data-testid="q-partner">
                                    <option value="">選択</option>
                                    {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="prc-label-overline block mb-1">対象車両 *</label>
                                <VehicleQrField
                                    value={qForm.vehicle_id}
                                    vehicles={vehicles}
                                    onPick={(v) => setQForm({ ...qForm, vehicle_id: v.id })}
                                    required
                                    testid="q-vehicle"
                                />
                            </div>
                            <F label="内容 *" value={qForm.description} onChange={(v) => setQForm({ ...qForm, description: v })} required testid="q-desc" />
                            <F label="概算金額（円）" type="number" value={qForm.amount_yen} onChange={(v) => setQForm({ ...qForm, amount_yen: v })} testid="q-amount" />
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button type="button" onClick={() => setQOpen(false)} className="prc-btn-ghost">キャンセル</button>
                            <button type="submit" className="prc-btn-primary">依頼</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function F({ label, value, onChange, type = "text", required, testid }) {
    return (
        <div>
            <label className="prc-label-overline block mb-1">{label}</label>
            <input type={type} required={required} className="prc-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        </div>
    );
}
